import { useCallback, useEffect, useState } from 'react'
import { api, type Usuario } from './api'
import { DefinicoesCamuflagem, FundoCamuflado } from './componentes/Camuflagem'
import { Login } from './paginas/Login'
import { Dashboard } from './paginas/Dashboard'
import { Admin } from './paginas/Admin'

/**
 * O esqueleto: decide entre login, dashboard e admin.
 *
 * **Não existe tela pública** — o painel tem meta e venda por marca, mais
 * pedido individual. Antes de qualquer coisa o `GET /api/sessao` responde quem
 * está do outro lado; sem sessão, só a tela de login.
 *
 * Roteamento sem biblioteca, por `location.pathname`: são duas telas, e
 * `react-router` para isso é dependência a mais para quebrar na véspera. O
 * `popstate` cobre o botão voltar.
 */

type Tela = 'dashboard' | 'admin'

function telaDoCaminho(caminho: string): Tela {
  return caminho.startsWith('/admin') ? 'admin' : 'dashboard'
}

export function Aplicacao() {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  /* `carregando` é um terceiro estado, não `usuario === null`: sem ele a tela de
     login pisca por um instante para quem JÁ está logado — e no telão, que
     recarrega sozinho, esse piscar é visível na sala. */
  const [carregando, setCarregando] = useState(true)
  const [tela, setTela] = useState<Tela>(() => telaDoCaminho(location.pathname))

  const conferirSessao = useCallback(async () => {
    try {
      const { usuario } = await api.sessao()
      setUsuario(usuario)
    } catch {
      setUsuario(null)
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => { void conferirSessao() }, [conferirSessao])

  useEffect(() => {
    const aoVoltar = () => setTela(telaDoCaminho(location.pathname))
    window.addEventListener('popstate', aoVoltar)
    return () => window.removeEventListener('popstate', aoVoltar)
  }, [])

  const navegar = useCallback((destino: Tela) => {
    history.pushState(null, '', destino === 'admin' ? '/admin' : '/')
    setTela(destino)
  }, [])

  /* Qual tela está REALMENTE na frente. O quiosque do telão não tem admin, e
     quem não é admin cai no dashboard mesmo pedindo `/admin` — a conta vive
     aqui porque é a mesma que decide a trava de rolagem, abaixo, e manter as
     duas separadas era como o admin acabava travado junto com o telão. */
  const mostrandoAdmin = usuario !== null && tela === 'admin' && usuario.papel === 'admin'

  /* Projetor NÃO ROLA: o telão fica preso em `100dvh`, com o que não caber
     recortado. Isso vale só para ele — no admin a aba Marcas passa da viewport
     e precisa rolar. A classe sai no desmonte para a trava não sobreviver à
     navegação de volta. */
  useEffect(() => {
    const travar = !carregando && usuario !== null && !mostrandoAdmin
    document.documentElement.classList.toggle('sem-rolagem', travar)
    return () => document.documentElement.classList.remove('sem-rolagem')
  }, [carregando, usuario, mostrandoAdmin])

  const sair = useCallback(async () => {
    try { await api.sair() } catch { /* sair sempre leva à tela de login, mesmo
      que a chamada falhe: o cookie pode já ter expirado, e prender a pessoa na
      tela por causa disso não ajuda ninguém */ }
    setUsuario(null)
    navegar('dashboard')
  }, [navegar])

  return (
    <>
      <DefinicoesCamuflagem />
      <FundoCamuflado />

      {carregando ? null : !usuario ? (
        <Login aoEntrar={setUsuario} />
      ) : mostrandoAdmin ? (
        <Admin usuario={usuario} aoSair={sair} aoVoltar={() => navegar('dashboard')} />
      ) : (
        /* O quiosque do telão é PRESO ao dashboard: sessão longa, sem admin. A
           barragem de verdade é no servidor; esta é só para a tela não oferecer
           um caminho que vai dar 403. */
        <Dashboard
          usuario={usuario}
          aoAbrirAdmin={usuario.papel === 'admin' ? () => navegar('admin') : null}
          aoSair={sair}
        />
      )}
    </>
  )
}
